import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Stickyfill from 'stickyfill';
import { isArray } from 'util';
import { connect } from 'react-redux';
import get from 'lodash.get';
import i18next from 'i18next';

import {
  article as ArticlePropType,
  socialMediaProfiles as socialPropType,
} from 'lib/CustomPropTypes';
import { attribution } from 'lib/social';
import { listenForHFSBump, disconnectHFSBump } from 'lib/listenForHFSBump';
import { reduceCSL } from 'lib/arrayUtils';
import { shouldRenderRegularBlog } from 'lib/liveBlog';
import BTE from 'lib/BTE';
import Breakpoints from 'lib/Breakpoints';
import getWorkbenchByline from 'lib/getWorkbenchByline';
import {
  getEcommerceArticleType,
  shouldDisplayEcommerceDisclaimerAlternateFormat,
} from 'lib/articleUtilities';
import { isTelemundoVertical } from 'lib/vertical';

import Ad from 'components/Ad';
import Blog from 'components/Blog';
import Byline from 'components/Article/Byline';
import EcommerceDisclaimer from 'components/EcommerceDisclaimer';

import LiveBlog from 'components/LiveBlog';
import MidresponsiveAd from 'components/Ad/Midresponsive';
import Recommended from 'components/Recommended';
import SocialShareList from 'components/Social/ShareList';
import ContentTimestamp from 'components/ContentTimestamp';
import Taboola from 'components/Taboola';

import { ArticleFoot } from '../ArticleFoot';
import getBodyValue from '../getBodyValue';
import getSections from '../getSections';

// Temporarily leaving this stylesheet to handle grid only styles
// until different solution for grid is implemented within new theming strategy
import styles from './styles.module.scss';

const block = 'article-body';

const disableRecommendations = {
  food: true,
};

const mapStateToProps = ({ shared }) => ({
  socialMediaProfiles: shared.socialMediaProfiles,
});

@connect(mapStateToProps)
class ArticleBody extends React.Component {
  ads = [];

  HFSObserver = null;

  static propTypes = {
    article: ArticlePropType.isRequired,
    vertical: PropTypes.string.isRequired,
    isChromeless: PropTypes.bool,
    path: PropTypes.string.isRequired,
    socialMediaProfiles: socialPropType.isRequired,
    shouldRenderRightRailTaboola: PropTypes.func,
    getRightRailAdConfig: PropTypes.func,
  };

  static contextTypes = {
    amp: PropTypes.bool,
    isLiveBlog: PropTypes.bool,
  };

  static childContextTypes = {
    canonicalUrl: PropTypes.string,
    setStickyVideoFlag: PropTypes.func,
  };

  static defaultProps = {
    isChromeless: false,
    shouldRenderRightRailTaboola: null,
    getRightRailAdConfig: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isScrollingUp: false,
      showTaboola: false,
      boxflexRendered: false,
      hasStickyVideo: false,
    };
  }

  getChildContext() {
    const { article } = this.props;
    return {
      setStickyVideoFlag: this.setStickyVideoFlag,
      canonicalUrl: article
        && article.url
        && article.url.primary,
    };
  }

  componentDidMount() {
    BTE.on('articleInView', this.monitorArticleView);

    if (this.ads) {
      this.stickyfill = Stickyfill();
      this.ads.forEach((ad) => this.stickyfill.add(ad));
      this.HFSObserver = listenForHFSBump(this, 'isScrollingUp');
    }
  }

  componentWillUnmount() {
    BTE.remove('articleInView', this.monitorArticleView);

    if (this.ads) {
      this.ads.forEach((ad) => this.stickyfill.remove(ad));
      disconnectHFSBump(this.HFSObserver);
    }
  }

  setStickyVideoFlag = (hasStickyVideo) => {
    this.setState({ hasStickyVideo });
  }

  // Comma separated list of authors with job titles.
  getAuthors = (authors) => {
    const { vertical } = this.props;
    return authors
      .map((author, index) => {
        const { name, jobTitle } = author.person;

        // Bylines with titles only on Think.
        if (vertical === 'think' && jobTitle.length) {
          // Comma separated if multiple
          const title = jobTitle.reduce(reduceCSL);
          // Comma after title and before 'and' between 2nd-to-last and last author
          const sep = (authors.length >= 3 && index === authors.length - 2) ? ',' : '';
          return `${name.trim()}, ${title}${sep}`;
        }
        if (name.startsWith('<component')) {
          return getWorkbenchByline(name);
        }
        // Just author name.
        return name.trim();
      })
      .reduce(reduceCSL);
  }

  getStandardByline = () => {
    if (this.isShellArticle()) {
      return null;
    }
    const {
      article: {
        dateCreated,
        datePublished,
        source,
        authors: articleAuthors,
        ecommerceEnabled,
      },
    } = this.props;

    const { isLiveBlog } = this.context;

    const authors = articleAuthors && articleAuthors
      .filter((author) => author.authorType === 'author');

    const includesAuthors = isArray(authors) && !!authors.length;
    const showAuthors = !isLiveBlog && includesAuthors;

    return (
      <section className={classNames({ mb7: !ecommerceEnabled })}>
        <div className="mb1 founders-mono f2 lh-copy gray-80 ls-tight">
          <ContentTimestamp
            dateCreated={dateCreated}
            datePublished={datePublished}
            delimiter=", "
            meta
            showCreatedDate={!isLiveBlog}
          />
          {source && source.organization && (
            <span className="article-body__source">
              {(dateCreated || datePublished) ? ' / ' : ''}
              {i18next.t('Source')}
              {': '}
              {source.organization.name}
            </span>
          )}
        </div>
        {showAuthors && (
          <div className="founders-cond f6 lh-none" data-test="byline">
            {i18next.t('By')}
            {' '}
            {this.getAuthors(authors)}
          </div>
        )}
      </section>
    );
  }

  getExpandedByline = () => {
    if (this.isShellArticle()) {
      return null;
    }

    const {
      article,
      article: {
        presentation: {
          byline,
        } = {},
      },
    } = this.props;

    // A CMS/API condition can set authors to 'null'
    const articleAuthors = article.authors || [];

    const authors = articleAuthors.filter((author) => author.authorType === 'author');
    const contribs = articleAuthors.filter((author) => author.authorType === 'contributor');

    const hasByline = byline && byline.toLowerCase() === 'expanded' && isArray(authors) && !!authors.length;
    const hasContributors = isArray(contribs) && !!contribs.length;

    if (hasByline || hasContributors) {
      return (
        <div className="articleBylineContainer mt8">
          {hasByline
            && authors.map((author) => (
              <Byline
                key={author.person.name}
                person={author.person}
              />
            ))}
          {hasContributors && (
            <div className="article-body__contributors pv5 founders-mono f2 lh-copy gray-80 ls-tight">
              {contribs.map((author) => author.person.name)
                .reduce(reduceCSL)}
              {' '}
              contributed.
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  boxflexAdRenderCheck = (enigmaAd, { EVENTS }) => {
    enigmaAd.on(EVENTS.RENDER, (event) => {
      if (!event || event.empty) {
        return;
      }
      this.setState({
        boxflexRendered: true,
      });
    });
  }

  isShellArticle = () => {
    const { article } = this.props;
    if (!article) return false;
    const { presentation } = article;
    return presentation && presentation.style && presentation.style === 'shell';
  };

  shouldShowAd = () => {
    const {
      article: {
        nativeAd,
        adsEnabled,
      },
    } = this.props;
    return !nativeAd && adsEnabled;
  };

  shouldShowTaboola = () => {
    const {
      article: {
        nativeAd,
      },
    } = this.props;
    const { showTaboola } = this.state;
    const { amp } = this.context;
    // BENTO-9179: Taboola should display when ads don't, except on paid content
    return (!nativeAd) && (amp || showTaboola || Breakpoints.isS());
  };

  isAmpAndEcommerce = () => {
    const { amp } = this.context;
    const { article: { ecommerceEnabled } } = this.props;
    return amp && ecommerceEnabled;
  }

  isDealsPage = () => {
    const { article: { taxonomy } } = this.props;
    if (!taxonomy) return false;
    return taxonomy.subtopics.find(({ name }) => name.toLowerCase() === 'deals');
  }

  disableRecommendations = () => {
    const {
      article: {
        nativeAd,
        ecommerceEnabled,
        presentation: {
          hideRecommendations,
        },
        taxonomy,
        subType,
      } = {},
      vertical,
    } = this.props;

    return nativeAd
      || ecommerceEnabled
      || hideRecommendations
      || disableRecommendations[
        get(taxonomy, 'primarySection.name', '').toLowerCase()
      ]
      || this.isShellArticle()
      || subType === 'liveBlog'
      || subType === 'blog'
      || isTelemundoVertical(vertical);
  };

  shouldRenderEcommerceRecommendations = () => {
    const { article: { ecommerceEnabled, subType } } = this.props;

    const subTypeIsNotLiveBlogOrBlog = (subType !== 'liveBlog' || subType !== 'blog');

    return ecommerceEnabled && subTypeIsNotLiveBlogOrBlog;
  }

  shouldRenderTodayGiftGuide = () => {
    const { article: { ecommerceEnabled, subType }, vertical } = this.props;

    const subTypeIsNotLiveBlogOrBlog = (subType !== 'liveBlog' || subType !== 'blog');
    const isTodayVertical = (vertical === 'today');

    return ecommerceEnabled && isTodayVertical && subTypeIsNotLiveBlogOrBlog;
  }

  socialShare = () => {
    const { amp } = this.context;
    if (!amp) {
      return null;
    }

    const { article, socialMediaProfiles } = this.props;
    const { headline, url } = article;
    const shareAttrib = attribution(socialMediaProfiles);
    const text = encodeURIComponent(headline.social);
    const smsBody = encodeURIComponent(`${headline.primary} via ${shareAttrib} - ${url.primary}`);

    return (
      <SocialShareList
        facebookHref={`https://www.facebook.com/sharer/sharer.php?u=${url.primary}`}
        twitterHref={`https://twitter.com/intent/tweet?text=${text}&via=${shareAttrib}&url=${url.primary}&original_referer=${url.primary}`}
        emailHref={`mailto:?subject=${headline.primary}&body=${url.primary}`}
        smsHref={`sms:?iOSfix&body=${smsBody}`}
        classes="df justify-start article-body__amp-social-share--wrapper"
        displayOptions={{ isFullWidthMobile: true, useColoredIcons: amp }}
        tracking="mbt_navbar_socialShare"
      />
    );
  }

  monitorArticleView = (id) => {
    const { article } = this.props;
    if (id === article.id) {
      this.setState({ showTaboola: true });
    }
  }

  registerAd(el) {
    const ad = el && el.querySelector('.article-body__right-rail-ad');
    if (ad) {
      this.ads = [...this.ads, ad];
    }
  }

  taboola(isRail = false) {
    if (this.isShellArticle()) {
      return null;
    }

    const { article: { id, url, ecommerceEnabled } } = this.props;

    const className = isRail && ecommerceEnabled ? 'dn-l' : null;

    if (this.shouldShowTaboola()) {
      return (
        <Taboola
          id={id}
          canonicalUrl={url.canonical || url.primary}
          isRail={isRail}
          className={className}
          ecommerceEnabled={ecommerceEnabled}
        />
      );
    }

    return null;
  }

  interstitialAd() {
    const { vertical } = this.props;

    if (this.shouldShowAd() && ['news', 'today'].includes(vertical)) {
      return (
        <Ad
          slot="interstitial"
          refreshInterval={0}
        />
      );
    }

    return null;
  }

  renderLiveBlog = () => {
    const { isLiveBlog } = this.context;

    if (isLiveBlog) {
      // Get taxonomy from body
      const { article: { body, subType } } = this.props;
      const taxonomy = body.filter((item) => item.type === 'embeddedTaxonomy')[0] || null;
      if (taxonomy) {
        return shouldRenderRegularBlog(subType)
          ? <Blog content={taxonomy} />
          : <LiveBlog content={taxonomy} />;
      }
    }
    return null;
  }

  renderSocialAndSponsored = ({
    bodyClasses, nativeAd, source,
  }) => {
    if (this.isShellArticle()) {
      return null;
    }

    return (
      <div className="article-body__section">
        <div className={bodyClasses}>
          {this.socialShare()}
          {nativeAd && source && source.copyright && (
            <p className="article-body__sponsored-by-text">{source && source.copyright}</p>
          )}
        </div>
      </div>
    );
  }

  renderRightRail = ({
    section, nativeAd, sectionIndex, articleContainsLargeProduct,
  }) => {
    const { ad = {} } = section;
    const {
      article: { url },
      shouldRenderRightRailTaboola,
      getRightRailAdConfig,
      vertical,
    } = this.props;
    const { boxflexRendered, isScrollingUp } = this.state;

    const isFirstSection = sectionIndex === 0;
    const isThirdSection = sectionIndex === 2;

    const isSectionForTodayRecommendationWidget = articleContainsLargeProduct
      ? isThirdSection : isFirstSection;

    const isNBCNewsVertical = vertical === 'news';
    const isTodayVertical = vertical === 'today';
    const isBetterVertical = vertical === 'better';
    const displayTodayEcommerceWidget = isSectionForTodayRecommendationWidget && isTodayVertical
      && this.shouldRenderEcommerceRecommendations();
    const displayNBCNewsEcommerceWidget = isNBCNewsVertical && this.shouldRenderEcommerceRecommendations(); // eslint-disable-line max-len
    const displayBetterEcommerceWidget = isBetterVertical && this.shouldRenderEcommerceRecommendations(); // eslint-disable-line max-len

    const adConfig = typeof getRightRailAdConfig === 'function'
      ? getRightRailAdConfig({
        section,
        nativeAd,
        sectionIndex,
        isFirstSection,
        isThirdSection,
      })
      : ad;
    const shouldRenderAd = adConfig.display && this.shouldShowAd() && !this.isAmpAndEcommerce();

    const shouldRenderTaboola = typeof shouldRenderRightRailTaboola === 'function'
      ? shouldRenderRightRailTaboola({
        section,
        nativeAd,
        sectionIndex,
        isFirstSection,
        isThirdSection,
      })
      : !this.isShellArticle() && section.taboola;

    if (shouldRenderTaboola || section.newsletter || shouldRenderAd
      || displayTodayEcommerceWidget) {
      return (
        <div
          className={classNames(
            styles.rightRail,
            'article-body__right-rail',
            {
              'article-body__right-rail-ecom di-l dn': displayTodayEcommerceWidget,
              HFSBumped: isScrollingUp,
            },
          )}
          ref={(el) => this.registerAd(el)}
        >
          {shouldRenderTaboola && this.taboola(true)}

          {displayNBCNewsEcommerceWidget && (
            <Recommended
              isNBCNewsCommerce
              url={url.primary}
              additionalClasses={classNames(
                'article-body__right-rail--recommended',
                {
                  'article-body__non-sticky': boxflexRendered,
                  pb1: boxflexRendered,
                },
              )}
              isRightRail
            />
          )}

          {displayBetterEcommerceWidget && (
            <Recommended
              isTrending
              isBetterNewsCommerce
              url={url.primary}
              additionalClasses={classNames(
                'article-body__right-rail--recommended',
                {
                  'article-body__non-sticky': boxflexRendered,
                  pb1: boxflexRendered,
                },
              )}
              isRightRail
            />
          )}

          {displayTodayEcommerceWidget
            && (
              <Recommended
                isTrending
                url={url.primary}
                additionalClasses={classNames(
                  'article-body__right-rail--recommended',
                  {
                    'article-body__non-sticky': boxflexRendered,
                    pb1: boxflexRendered,
                  },
                )}
                isRightRail
              />
            )}

          {shouldRenderAd
            && (
              <Ad
                slot={adConfig.slot}
                enumerator={adConfig.index}
                adClass="article-body__right-rail--ad"
                offset={10}
                handler={this.boxflexAdRenderCheck}
              />
            )}
        </div>
      );
    }

    return null;
  }

  render() {
    const {
      article,
      isChromeless,
      path,
      vertical,
    } = this.props;

    if (!article) {
      return null;
    }

    const { amp, isLiveBlog } = this.context;
    const { hasStickyVideo, showTaboola } = this.state;
    const {
      adsEnabled,
      ecommerceMetadata,
      ecommerceEnabled,
      nativeAd,
      primaryImage,
      primaryMedia,
      source,
      url,
    } = article;

    const disableReco = this.disableRecommendations();
    const articleBody = getBodyValue(article);
    const articleContainsLargeProduct = !!articleBody.find(
      ({ type, presentation }) => type === 'embeddedProduct' && presentation.size === 'large',
    ) && !amp;

    const sections = getSections({
      articleBody,
      article,
      ecommerceEnabled,
      nativeAd,
      adsEnabled,
      disableReco,
      amp,
      path,
      vertical,
    });

    const sectionsIndex = sections.map((section, index) => ({ index, ...section }));
    const sectionLast = sections.length - 1;

    const gridContainerClasses = classNames(
      styles.gridContainer,
      'gridContainer',
      'article-body__grid--container',
      {
        'article-body__grid--container-no-main-image': !primaryImage && !primaryMedia,
        'article-body__ecommerce-enabled': ecommerceEnabled,
        'article-body__grid--container-live-blog': isLiveBlog,
      },
    );

    const bodyClasses = classNames(
      styles.body,
      'article-body',
      'publico-txt f4 f5-m lh-copy',
      {
        'gray-100': vertical !== 'today',
        'gray-80': vertical === 'today',
      },
    );

    const dataTaboolaTarget = !isChromeless && !isLiveBlog ? 'read-more' : '';

    const ecommerceArticleType = getEcommerceArticleType(vertical, article);
    const ecommerceDisclaimerVerbiage = get(ecommerceMetadata, 'disclaimer.body[0].html');
    /* eslint-disable-next-line max-len */
    const useEcommerceDisclaimerAlternateFormat = shouldDisplayEcommerceDisclaimerAlternateFormat(ecommerceArticleType);

    const isNBCNewsVertical = vertical === 'news';
    const isTodayVertical = vertical === 'today';
    const isBetterVertical = vertical === 'better';
    const isOnNBCNews = isNBCNewsVertical && this.shouldRenderEcommerceRecommendations();
    const isOnBetterNews = isBetterVertical && this.shouldRenderEcommerceRecommendations();
    const isOnTodayNews = isTodayVertical && this.shouldRenderEcommerceRecommendations();

    return (
      <>
        <article className={block} data-taboola-target={dataTaboolaTarget}>

          <div className={gridContainerClasses}>
            {!this.isAmpAndEcommerce() && this.interstitialAd()}

            {this.renderSocialAndSponsored({ bodyClasses, nativeAd, source })}

            {sectionsIndex.map((section, i) => (
              <div
                key={section.index}
                className={classNames(
                  'article-body__section',
                  { 'article-body__last-section': i === sections.length - 1 },
                )}
              >
                <div className={bodyClasses} data-test="articleBody">
                  {i === 0 && (
                    <>
                      {this.getStandardByline()}

                      {ecommerceEnabled && (
                        <EcommerceDisclaimer
                          alternateFormat={useEcommerceDisclaimerAlternateFormat}
                          verbiage={ecommerceDisclaimerVerbiage}
                        />
                      )}
                    </>
                  )}
                  <div
                    className={
                      classNames('article-body__content', { 'article-body__content--sticky-video': hasStickyVideo })
                    }
                  >
                    {/* Render section components */}
                    {section.items}

                    {/* Render live blog in last article section */}
                    {i === sectionLast
                      && isLiveBlog
                      && (
                        this.renderLiveBlog()
                      )}
                  </div>
                  {i === sectionLast && (
                    <>
                      {this.getExpandedByline()}
                    </>
                  )}
                </div>
                {this.renderRightRail({
                  section, nativeAd, sectionIndex: i, articleContainsLargeProduct,
                })}
              </div>
            ))}

            {isOnNBCNews && (
              <Recommended
                url={url.primary}
                isNBCNewsCommerce
                additionalClasses={classNames(styles.body, 'dn-l')}
                sticky={false}
              />
            )}

            {isOnBetterNews && (
              <Recommended
                url={url.primary}
                isTrending
                isBetterNewsCommerce
                additionalClasses={classNames(styles.body, 'dn-l')}
                sticky={false}
              />
            )}

            {isOnTodayNews && (
              <Recommended
                url={url.primary}
                isTrending
                additionalClasses={classNames(styles.body, 'article-body article-body__bottom-rail--recommended')}
                sticky={false}
              />
            )}

            {
              this.shouldShowAd()
              && !this.isAmpAndEcommerce()
              && !showTaboola
              && <MidresponsiveAd />
            }

            {this.shouldRenderTodayGiftGuide() && (
              <Recommended
                url={url.primary}
                isGiftGuide
                additionalClasses={classNames(styles.body, 'article-body')}
                sticky={false}
              />
            )}
          </div>
        </article>

        <ArticleFoot article={article} />

        {/* TODO? move to ArticleFoot */}
        {this.taboola()}
      </>
    );
  }
}

export default ArticleBody;
